import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, Check, X, Navigation, Loader2, AlertCircle } from 'lucide-react';
import { config } from '../../config/config';
import { validateAddress, type AddressValidationResult } from '../../services/addressValidationService';
import { useTenantData } from '../../contexts/TenantDataContext';
import { getRegionLabel, getPostalCodeLabel } from '../../utils/addressRegions';
import { extractCityFromComponents } from '../../utils/googleAddressComponents';

interface AddressData {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;
  coordinates?: { lat: number; lng: number };
  validation?: AddressValidationResult;
}

interface AddressInputWithMapProps {
  value: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  coordinates?: { lat: number; lng: number } | null;
  onChange: (addressData: AddressData) => void;
  onInputChange?: (value: string) => void;
  onAddress2Change?: (value: string) => void;
  onCityChange?: (value: string) => void;
  onStateChange?: (value: string) => void;
  onZipCodeChange?: (value: string) => void;
  onValidationComplete?: (result: AddressValidationResult) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  cityError?: string;
  stateError?: string;
  zipCodeError?: string;
  className?: string;
  showMap?: boolean;
  mapHeight?: number;
  enableValidation?: boolean;
}

const AddressInputWithMap: React.FC<AddressInputWithMapProps> = ({
  value,
  address2 = '',
  city = '',
  state = '',
  zipCode = '',
  coordinates,
  onChange,
  onInputChange,
  onAddress2Change,
  onCityChange,
  onStateChange,
  onZipCodeChange,
  onValidationComplete,
  placeholder = 'Start typing your address...',
  required = false,
  disabled = false,
  error,
  cityError,
  stateError,
  zipCodeError,
  className = '',
  showMap = true,
  mapHeight = 180,
  enableValidation = true
}) => {
  const { country: tenantCountry } = useTenantData();
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const onChangeRef = useRef(onChange);
  const onValidationCompleteRef = useRef(onValidationComplete);
  const isInitializedRef = useRef(false);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isLoadingMap, setIsLoadingMap] = useState(false);
  
  // Validation state
  const [validationState, setValidationState] = useState<{
    status: 'idle' | 'validating' | 'validated' | 'fallback';
    confidence?: 'high' | 'medium' | 'low' | 'unknown';
    message?: string;
  }>({ status: 'idle' });

  // Keep refs updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  useEffect(() => {
    onValidationCompleteRef.current = onValidationComplete;
  }, [onValidationComplete]);
  
  // Validate address after selection
  const performValidation = useCallback(async (addressData: Omit<AddressData, 'validation'>) => {
    if (!enableValidation) return;

    setValidationState({ status: 'validating' });

    try {
      const result = await validateAddress({
        addressLine1: addressData.address1,
        addressLine2: addressData.address2,
        city: addressData.city,
        state: addressData.state,
        zipCode: addressData.zipCode,
        country: tenantCountry
      });
      
      setValidationState({
        status: result.validationStatus === 'fallback' ? 'fallback' : 'validated',
        confidence: result.confidence,
        message: result.message
      });
      
      // Notify parent of validation result
      onValidationCompleteRef.current?.(result);
      
      // Update address with validation data and corrected address if available
      if (result.correctedAddress && result.confidence === 'high') {
        onChangeRef.current({
          ...addressData,
          address1: result.correctedAddress.addressLine1,
          city: result.correctedAddress.city,
          state: result.correctedAddress.state,
          zipCode: result.correctedAddress.zipCode,
          coordinates: result.coordinates || addressData.coordinates,
          validation: result
        });
      } else {
        onChangeRef.current({
          ...addressData,
          coordinates: result.coordinates || addressData.coordinates,
          validation: result
        });
      }
    } catch (error) {
      console.error('Address validation error:', error);
      const fallbackResult: AddressValidationResult = {
        isValid: true,
        validationStatus: 'fallback',
        confidence: 'unknown',
        message: 'Validation unavailable'
      };
      setValidationState({ 
        status: 'fallback',
        message: 'Validation unavailable'
      });
      // Notify parent so they can update their state
      onValidationCompleteRef.current?.(fallbackResult);
    }
  }, [enableValidation, tenantCountry]);

  // Load Google Maps
  useEffect(() => {
    if (window.google?.maps?.places?.Autocomplete) {
      setIsGoogleLoaded(true);
      return;
    }

    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`) as HTMLScriptElement;
    
    if (existingScript) {
      let attempts = 0;
      const maxAttempts = 100;
      
      const checkGoogleLoaded = setInterval(() => {
        attempts++;
        if (window.google?.maps?.places?.Autocomplete && window.google?.maps?.marker?.AdvancedMarkerElement) {
          clearInterval(checkGoogleLoaded);
          setIsGoogleLoaded(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkGoogleLoaded);
          console.error('Google Maps failed to load');
        }
      }, 100);

      return () => clearInterval(checkGoogleLoaded);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.googleMapsApiKey}&libraries=places,marker`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => setIsGoogleLoaded(true), 100);
    };
    document.head.appendChild(script);
  }, []);

  // Initialize autocomplete
  useEffect(() => {
    if (!isGoogleLoaded || isInitializedRef.current || !inputRef.current) return;

    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: ['us', 'ca'] },
          fields: ['address_components', 'formatted_address', 'geometry', 'name']
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry?.location || !place?.address_components) return;

        let streetNumber = '';
        let route = '';
        let subpremise = '';
        let stateValue = '';
        let zipCodeValue = '';

        place.address_components.forEach((component) => {
          const types = component.types;

          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            route = component.long_name;
          }
          if (types.includes('subpremise')) {
            subpremise = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            stateValue = component.short_name;
          }
          // ZIP Code - check multiple types for different formats
          if (!zipCodeValue && types.includes('postal_code')) {
            zipCodeValue = component.long_name;
          } else if (!zipCodeValue && types.includes('postal_code_suffix')) {
            zipCodeValue = component.long_name;
          } else if (!zipCodeValue && types.includes('postal_code_prefix')) {
            zipCodeValue = component.long_name;
          }
        });
        
        const cityValue = extractCityFromComponents(
          place.address_components.map((component) => ({
            types: component.types,
            name: component.long_name,
          }))
        );

        // Fallback: extract postal code from formatted address if not found in components.
        // Matches a US ZIP (5 or 5+4) or a Canadian postal code (A1A 1A1, separator optional).
        if (!zipCodeValue && place.formatted_address) {
          const zipMatch = place.formatted_address.match(
            /\b(\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d)\b/
          );
          if (zipMatch) {
            zipCodeValue = zipMatch[1];
          }
        }

        let address1 = '';
        if (streetNumber && route) {
          address1 = `${streetNumber} ${route}`;
        } else if (route) {
          address1 = route;
        } else if (place.formatted_address) {
          const parts = place.formatted_address.split(',');
          if (parts.length > 0) {
            address1 = parts[0].trim();
          }
        }

        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };

        const addressData: Omit<AddressData, 'validation'> = {
          address1: address1.trim(),
          address2: subpremise || undefined,
          city: cityValue,
          state: stateValue,
          zipCode: zipCodeValue,
          coordinates: coords
        };

        // Call onChange immediately with Places data
        onChangeRef.current(addressData);
        
        // Reset validation state and perform validation
        setValidationState({ status: 'idle' });
        performValidation(addressData);
      });

      isInitializedRef.current = true;
    } catch (error) {
      console.error('Error initializing Google Places:', error);
    }

    return () => {
      if (autocompleteRef.current) {
        try {
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [isGoogleLoaded]);

  // Initialize and update map when coordinates change
  useEffect(() => {
    if (!isGoogleLoaded || !showMap || !mapRef.current || !coordinates) return;

    setIsLoadingMap(true);

    const initOrUpdateMap = () => {
      if (!mapInstanceRef.current) {
        // Create the map
        mapInstanceRef.current = new google.maps.Map(mapRef.current!, {
          center: coordinates,
          zoom: 16,
          mapId: 'address-preview-map',
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
          clickableIcons: false
        });
      } else {
        // Update existing map
        mapInstanceRef.current.setCenter(coordinates);
      }

      // Remove old marker
      if (markerRef.current) {
        markerRef.current.map = null;
      }

      // Create new marker
      const markerContent = document.createElement('div');
      markerContent.innerHTML = `
        <div style="
          background: linear-gradient(135deg, #6366f1, #3b82f6);
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          transform: translateY(-22px);
        ">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </div>
        <div style="
          width: 12px;
          height: 12px;
          background: #6366f1;
          border-radius: 50%;
          margin: -8px auto 0;
          box-shadow: 0 2px 6px rgba(99, 102, 241, 0.4);
        "></div>
      `;

      markerRef.current = new google.maps.marker.AdvancedMarkerElement({
        map: mapInstanceRef.current,
        position: coordinates,
        content: markerContent
      });

      setIsLoadingMap(false);
    };

    // Small delay to ensure the map container is visible
    setTimeout(initOrUpdateMap, 100);

    return () => {
      // Don't destroy the map on every update, just on unmount
    };
  }, [isGoogleLoaded, showMap, coordinates]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
      }
      mapInstanceRef.current = null;
    };
  }, []);

  const isVerified = coordinates != null;

  return (
    <div className="space-y-4">
      {/* Google Places Autocomplete Styles */}
      <style>{`
        .pac-container {
          background-color: white !important;
          border-radius: 12px !important;
          border: 1px solid rgb(209, 213, 219) !important;
          margin-top: 4px !important;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
          font-family: inherit !important;
          z-index: 9999 !important;
          padding: 4px !important;
          overflow: hidden !important;
        }
        .pac-item {
          background-color: white !important;
          padding: 12px 14px !important;
          border-top: none !important;
          border-radius: 8px !important;
          margin: 2px 0 !important;
          cursor: pointer !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          color: rgb(17, 24, 39) !important;
          display: flex !important;
          align-items: center !important;
        }
        .pac-item:hover {
          background-color: rgb(243, 244, 246) !important;
        }
        .pac-item-selected {
          background-color: rgb(238, 242, 255) !important;
        }
        .pac-icon {
          margin-right: 12px !important;
          margin-top: 0 !important;
        }
        .pac-item-query {
          color: rgb(17, 24, 39) !important;
          font-size: 14px !important;
          font-weight: 500 !important;
        }
        .pac-matched {
          color: rgb(79, 70, 229) !important;
          font-weight: 600 !important;
        }
      `}</style>

      {/* Street Address Input */}
      <div>
        <label className="block text-xs font-semibold text-ink-700 mb-1.5">
          Street Address {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onInputChange?.(e.target.value)}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className={`w-full pl-10 pr-10 py-2.5 border rounded-xl text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
              error ? 'border-red-300 bg-red-50' : 'border-ink-200 hover:border-ink-300'
            } ${className}`}
          />
          {isVerified && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="flex items-center justify-center w-5 h-5 bg-emerald-500 rounded-full">
                <Check className="h-3 w-3 text-white stroke-[3]" />
              </div>
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        {isVerified && (
          <p className={`mt-1.5 text-xs flex items-center gap-1 ${
            validationState.status === 'validated' && validationState.confidence === 'high'
              ? 'text-emerald-600'
              : validationState.status === 'validating'
                ? 'text-blue-600'
                : validationState.status === 'fallback'
                  ? 'text-ink-500'
                  : validationState.confidence === 'low'
                    ? 'text-amber-600'
                    : 'text-emerald-600'
          }`}>
            {validationState.status === 'validating' ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Validating address...
              </>
            ) : validationState.status === 'validated' && validationState.confidence === 'high' ? (
              <>
                <Navigation className="h-3 w-3" />
                Address verified ✓
              </>
            ) : validationState.status === 'validated' && validationState.confidence === 'low' ? (
              <>
                <AlertCircle className="h-3 w-3" />
                {validationState.message || 'Address may need correction'}
              </>
            ) : validationState.status === 'validated' ? (
              <>
                <Navigation className="h-3 w-3" />
                Address located {validationState.message ? `(${validationState.message})` : ''}
              </>
            ) : validationState.status === 'fallback' ? (
              <>
                <AlertCircle className="h-3 w-3" />
                Location found - validation unavailable
              </>
            ) : validationState.confidence === 'low' ? (
              <>
                <AlertCircle className="h-3 w-3" />
                {validationState.message || 'Please verify address details'}
              </>
            ) : (
              <>
                <Navigation className="h-3 w-3" />
                Address verified with coordinates
              </>
            )}
          </p>
        )}
      </div>

      {/* Address Line 2 */}
      <div>
        <label className="block text-xs font-semibold text-ink-700 mb-1.5">
          Apt, Suite, Unit <span className="text-ink-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={address2}
          onChange={(e) => onAddress2Change?.(e.target.value)}
          placeholder="Apt 4B"
          disabled={disabled}
          className="w-full px-4 py-2.5 border border-ink-200 hover:border-ink-300 rounded-xl text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        />
      </div>

      {/* City, State, Zip Row */}
      <div className="grid grid-cols-5 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-ink-700 mb-1.5">City</label>
          <input
            type="text"
            value={city}
            onChange={(e) => onCityChange?.(e.target.value)}
            disabled={disabled}
            className={`w-full px-4 py-2.5 border rounded-xl text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
              cityError ? 'border-red-300 bg-red-50' : 'border-ink-200 hover:border-ink-300'
            }`}
          />
          {cityError && <p className="mt-1 text-xs text-red-600">{cityError}</p>}
        </div>
        <div className="col-span-1">
          <label className="block text-xs font-semibold text-ink-700 mb-1.5">{getRegionLabel(tenantCountry)}</label>
          <input
            type="text"
            value={state}
            onChange={(e) => onStateChange?.(e.target.value)}
            disabled={disabled}
            maxLength={2}
            className={`w-full px-4 py-2.5 border rounded-xl text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
              stateError ? 'border-red-300 bg-red-50' : 'border-ink-200 hover:border-ink-300'
            }`}
          />
          {stateError && <p className="mt-1 text-xs text-red-600">{stateError}</p>}
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-semibold text-ink-700 mb-1.5">{getPostalCodeLabel(tenantCountry)}</label>
          <input
            type="text"
            value={zipCode}
            onChange={(e) => onZipCodeChange?.(e.target.value)}
            disabled={disabled}
            className={`w-full px-4 py-2.5 border rounded-xl text-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
              zipCodeError ? 'border-red-300 bg-red-50' : 'border-ink-200 hover:border-ink-300'
            }`}
          />
          {zipCodeError && <p className="mt-1 text-xs text-red-600">{zipCodeError}</p>}
        </div>
      </div>

      {/* Map Preview */}
      {showMap && coordinates && (
        <div className="relative overflow-hidden rounded-xl border border-ink-200 bg-ink-50">
          {isLoadingMap && (
            <div className="absolute inset-0 flex items-center justify-center bg-ink-100 z-10">
              <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
            </div>
          )}
          <div 
            ref={mapRef} 
            style={{ height: mapHeight }}
            className="w-full"
          />
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-surface/90 backdrop-blur-sm rounded-lg shadow-sm">
            <p className="text-2xs text-ink-600 font-medium">
              {value}{address2 ? `, ${address2}` : ''}, {city}, {state} {zipCode}
            </p>
          </div>
        </div>
      )}

      {/* Placeholder when no coordinates yet */}
      {showMap && !coordinates && value && (
        <div 
          className="flex items-center justify-center rounded-xl border-2 border-dashed border-ink-200 bg-ink-50/50"
          style={{ height: mapHeight }}
        >
          <div className="text-center">
            <MapPin className="h-8 w-8 text-ink-300 mx-auto mb-2" />
            <p className="text-sm text-ink-400">Select an address from the dropdown</p>
            <p className="text-xs text-ink-400">to see the location preview</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressInputWithMap;
